import React from 'react';
import { Box, Typography, makeStyles } from '@material-ui/core';
import { AtCircularProgress } from '../../../components/AtCircularProgress';
import { AtNoData } from '../../../components/AtNoData';
import { useTranslation } from 'react-i18next';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import wordcloud from 'highcharts/modules/wordcloud';

// Initialize the wordcloud module
if (typeof Highcharts === 'object') {
    wordcloud(Highcharts);
}

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: '#FFFFFF',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        padding: theme.spacing(3),
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        '& .MuiCircularProgress-colorPrimary': {
            color: '#6366F1',
        },
    },
    title: {
        fontSize: '10px',
        fontWeight: 700,
        color: '#6B7280',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        textAlign: 'center',
        marginBottom: theme.spacing(3),
    },
    chartContainer: {
        flex: 1,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    }
}));

const WordCloudChart = ({ data, isLoading }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    
    const rawData = data || [];
    const maxVal = rawData.length > 0 ? Math.max(...rawData.map(d => d.value)) : 1;
    const minVal = rawData.length > 0 ? Math.min(...rawData.map(d => d.value)) : 0;

    // Highcharts Wordcloud requires 'name' (the word) and 'weight'
    const safeData = rawData.map(item => {
        const intensity = maxVal === minVal ? 1 : (item.value - minVal) / (maxVal - minVal);
        const alpha = 0.3 + (0.7 * intensity);
        
        return {
            name: item.text,
            weight: item.value,
            color: `rgba(220, 38, 38, ${alpha.toFixed(2)})`
        };
    });

    const options = {
        chart: {
            type: 'wordcloud',
            height: 300,
            style: { fontFamily: 'inherit' }
        },
        title: { text: null },
        credits: { enabled: false },
        tooltip: {
            pointFormat: `${t('sentiment.charts.mentions')}: <b>{point.weight}</b>`
        },
        series: [{
            type: 'wordcloud',
            data: safeData,
            name: t('sentiment.charts.occurrences'),
            rotation: {
                from: 0,
                to: 0,
                orientations: 1
            },
            style: {
                fontFamily: 'inherit',
                fontWeight: '600'
            }
        }]
    };

    return (
        <Box className={classes.root}>
            <Typography className={classes.title}>{t('sentiment.charts.wordCloudTitle')}</Typography>
            {isLoading ? (
                <AtCircularProgress />
            ) : safeData.length === 0 ? (
                <AtNoData />
            ) : (
                <Box className={classes.chartContainer}>
                    <HighchartsReact highcharts={Highcharts} options={options} />
                </Box>
            )}
        </Box>
    );
};

export default WordCloudChart;
